import { useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Download, ExternalLink, FileWarning, Loader2, Pencil } from 'lucide-react'
import { Button, Card } from '@/components/ui'
import { TrainerReviewResultSummary } from '@/components/pedagogy/TrainerReviewResultSummary'
import { renderFormattedComment } from '@/lib/commentFormatting'
import { TRAINER_CHECKLIST_SECTIONS } from '@/lib/trainerChecklist'
import { formatTrainerReviewDateTime, type TrainerChecklistPhoto } from '@/lib/trainerChecklistReviews'
import { useStudioStore } from '@/store'
import { getPartnerTrainerReviewDetail } from '@/services/partnerTeachersService'
import { getTrainerReviewPhotoUrl, resolveTrainerReviewPhotoUrls } from '@/services/trainerReviewPhotoUrls'
import { getSeniorTeacherReviewEditAccess, type SeniorTeacherReviewEditAccess } from '@/services/seniorTeacherReviewService'
import type { TrainerReviewDetail } from '@/services/trainerReviewService'

function canViewTeachers(accessKind: string | undefined): boolean {
  return accessKind === 'owner' || accessKind === 'manager' || accessKind === 'management_company' || accessKind === 'senior_teacher'
}

function getItemScoreTone(score: number | null | undefined, maxScore: number): string {
  if (score == null || maxScore <= 0) return 'border-slate-300 bg-white text-slate-700'
  if (score <= 0) return 'border-rose-200 bg-rose-50 text-rose-700'
  if (score >= maxScore) return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  return 'border-amber-200 bg-amber-50 text-amber-700'
}

function getCriterionPhotos(detail: TrainerReviewDetail, criterionCode: string): TrainerChecklistPhoto[] {
  return detail.criterionPhotos[criterionCode] ?? []
}

function allPhotos(detail: TrainerReviewDetail): TrainerChecklistPhoto[] {
  return [...detail.generalPhotos, ...Object.values(detail.criterionPhotos).flat()]
}

export function PartnerTrainerReviewPage() {
  const { partnerNumber = '', studioId = '', teacherId = '', reviewKey = '' } = useParams<{
    partnerNumber: string
    studioId: string
    teacherId: string
    reviewKey: string
  }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { studios, studiosSynced } = useStudioStore()
  const parsedStudioId = Number(studioId)
  const studio = studios.find(item => item.studio_internal_id === parsedStudioId)
  const isSeniorTeacher = studio?.role === 'senior_teacher' || studio?.access_kind === 'senior_teacher'
  const hasAccess = studio ? canViewTeachers(studio.access_kind) || studio.role === 'manager' || isSeniorTeacher : false

  const [detail, setDetail] = useState<TrainerReviewDetail | null>(null)
  const [seniorEditAccess, setSeniorEditAccess] = useState<SeniorTeacherReviewEditAccess | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [photosReady, setPhotosReady] = useState(false)

  useEffect(() => {
    document.body.classList.add('report-print-page')
    document.documentElement.classList.add('report-print-page')
    return () => {
      document.body.classList.remove('report-print-page')
      document.documentElement.classList.remove('report-print-page')
    }
  }, [])

  useEffect(() => {
    if (!Number.isInteger(parsedStudioId) || parsedStudioId <= 0 || !reviewKey || !hasAccess) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError('')

    Promise.all([
      getPartnerTrainerReviewDetail(parsedStudioId, reviewKey, { loadPhotoUrls: false }),
      isSeniorTeacher
        ? getSeniorTeacherReviewEditAccess(parsedStudioId, reviewKey)
        : Promise.resolve(null),
    ])
      .then(([data, editAccess]) => {
        if (cancelled) return
        if (!data) {
          setDetail(null)
          setSeniorEditAccess(null)
          setError('Отсмотр не найден или ещё недоступен.')
          return
        }
        setDetail(data)
        setSeniorEditAccess(editAccess)
      })
      .catch(loadError => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : 'Не удалось открыть отсмотр')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [hasAccess, isSeniorTeacher, parsedStudioId, reviewKey, studio?.access_kind])

  useEffect(() => {
    if (!detail) return

    const photos = allPhotos(detail).filter(photo => photo.storagePath)
    if (photos.length === 0) return

    let cancelled = false
    void resolveTrainerReviewPhotoUrls(photos.map(photo => ({
      storage_bucket: photo.storageBucket,
      storage_path: photo.storagePath,
    }))).then(resolved => {
      if (cancelled) return
      const hydrate = (photo: TrainerChecklistPhoto): TrainerChecklistPhoto => ({
        ...photo,
        previewUrl: getTrainerReviewPhotoUrl({
          storage_bucket: photo.storageBucket,
          storage_path: photo.storagePath,
        }, resolved),
      })
      setDetail(current => current
        ? {
            ...current,
            generalPhotos: current.generalPhotos.map(hydrate),
            criterionPhotos: Object.fromEntries(
              Object.entries(current.criterionPhotos).map(([code, codePhotos]) => [code, codePhotos.map(hydrate)])
            ),
          }
        : current)
    })

    return () => {
      cancelled = true
    }
  }, [detail?.reviewId])

  useEffect(() => {
    if (!detail) {
      setPhotosReady(false)
      return
    }

    const photos = allPhotos(detail)
    if (photos.length === 0) {
      setPhotosReady(true)
      return
    }

    let cancelled = false
    setPhotosReady(false)

    Promise.all(photos.map(photo => new Promise<void>(resolve => {
      const image = new Image()
      image.onload = () => resolve()
      image.onerror = () => resolve()
      image.src = photo.previewUrl
    }))).then(() => {
      if (!cancelled) setPhotosReady(true)
    })

    return () => {
      cancelled = true
    }
  }, [detail])

  const reportSections = useMemo(() => {
    if (!detail) return []
    return TRAINER_CHECKLIST_SECTIONS
      .map(section => ({
        ...section,
        items: section.items
          .map(item => ({
            ...item,
            score: detail.scores[item.id],
            comment: detail.comments[item.id]?.trim() ?? '',
            photos: getCriterionPhotos(detail, item.id),
          }))
          .filter(item => item.score != null),
      }))
      .filter(section => section.items.length > 0)
  }, [detail])

  const teacherPath = `/partners/${partnerNumber}/studios/${studioId}/teachers/${teacherId}`
  const returnToRaw = new URLSearchParams(location.search).get('returnTo') || ''
  const returnTo = returnToRaw.startsWith('/') && !returnToRaw.startsWith('//') ? returnToRaw : ''
  const handleBack = () => navigate(returnTo || teacherPath)

  if (studiosSynced && (!studio || !hasAccess)) {
    return <Navigate to={`/partners/${partnerNumber}/studios/${studioId}/analytics`} replace />
  }

  if (loading) {
    return (
      <div className="space-y-4 pb-8">
        <Card className="border border-border shadow-sm">
          <div className="text-sm text-muted-foreground">Открываем отсмотр…</div>
        </Card>
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div className="space-y-4 pb-8">
        <Button variant="outline" size="sm" onClick={handleBack}>
          <ArrowLeft className="mr-1.5 inline h-4 w-4" />
          {returnTo ? 'Назад' : 'К педагогу'}
        </Button>
        <Card className="border border-error/30 bg-error/5 shadow-sm">
          <div className="flex items-start gap-3 text-sm text-error">
            <FileWarning className="mt-0.5 h-4 w-4 shrink-0" />
            <div>{error || 'Не удалось открыть отсмотр.'}</div>
          </div>
        </Card>
      </div>
    )
  }


  return (
    <div className="trainer-review-report min-h-screen space-y-4 bg-white pb-8">
      <div className="report-screen-actions flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" size="sm" onClick={handleBack}>
          <ArrowLeft className="mr-1.5 inline h-4 w-4" />
          {returnTo ? 'Назад' : 'К педагогу'}
        </Button>
        <div className="flex items-center gap-2">
          {seniorEditAccess && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(
                `/partners/${partnerNumber}/studios/${studioId}/teachers/reviews/new?reviewKey=${encodeURIComponent(reviewKey)}&returnTo=${encodeURIComponent(returnTo || teacherPath)}`
              )}
            >
              <Pencil className="mr-1.5 inline h-4 w-4" />
              Редактировать
            </Button>
          )}
          {!photosReady && allPhotos(detail).length > 0 && (
            <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Загружаем фото
            </div>
          )}
          <Button size="sm" onClick={() => window.print()} disabled={!photosReady}>
            <Download className="mr-1.5 inline h-4 w-4" />
            Скачать PDF
          </Button>
        </div>
      </div>

      <section className="report-sheet mx-auto max-w-5xl space-y-4 bg-white p-0">
        <div className="overflow-hidden rounded-[28px] border border-slate-200/90 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
          <div className="h-2 bg-gradient-to-r from-amber-500 via-orange-400 to-emerald-500" />
          <div className="p-5 sm:p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Отсмотр</div>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Отчёт по тренировке</h1>
            <div className="report-meta-grid mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_240px] lg:items-stretch">
              <ReportMetaCard label="Город" value={detail.meta.city || '—'} className="report-meta-city lg:col-start-1 lg:row-start-1" />
              <ReportMetaCard label="Студия" value={detail.meta.studioAddress || '—'} className="report-meta-studio lg:col-start-2 lg:row-start-1" />
              <ReportMetaCard label="Педагог" value={detail.meta.coachName || '—'} className="report-meta-teacher lg:col-start-1 lg:row-start-2" />
              <ReportMetaCard
                label="Дата и время тренировки"
                value={formatTrainerReviewDateTime(detail.meta.trainingDateTime) || '—'}
                className="report-meta-datetime lg:col-start-2 lg:row-start-2"
              />
              <ReportMetaCard
                label="Направление"
                value={detail.meta.directionName || '—'}
                className="report-meta-direction sm:col-span-2 lg:col-start-1 lg:row-start-3 lg:col-span-2"
              />
              <TrainerReviewResultSummary
                totalScore={detail.totalScore}
                maxScore={detail.maxScore}
                qualityPercent={detail.qualityPercent}
                className="report-result-summary sm:col-span-2 lg:col-start-3 lg:row-start-2 lg:row-span-2"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {reportSections.map(section => (
            <section key={section.id} className="report-section space-y-3 rounded-2xl border border-slate-300 bg-slate-50/35 p-4 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
              <div className="report-section-header border-b border-slate-300 pb-3">
                <div className="flex items-center gap-3">
                  <span className="h-8 w-2 rounded-full bg-amber-500" />
                  <h2 className="text-lg font-semibold text-slate-900">{section.title}</h2>
                </div>
              </div>

              <div className="report-section-items space-y-3">
                {section.items.map(item => (
                  <article key={item.id} className="report-item rounded-xl border border-slate-300 bg-white p-4 shadow-[0_1px_0_rgba(15,23,42,0.03)]">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <h3 className="min-w-0 text-sm font-semibold leading-6 text-slate-900">{item.label}</h3>
                      <div className={`shrink-0 rounded-full border px-3 py-1.5 text-sm font-semibold tabular-nums ${getItemScoreTone(item.score, item.maxScore)}`}>
                        {item.score == null ? 'Не оценено' : `${item.score} / ${item.maxScore}`}
                      </div>
                    </div>

                    {item.comment && (
                      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/40 px-3 py-3 text-sm leading-6 text-slate-600">
                        {renderFormattedComment(item.comment)}
                      </div>
                    )}

                    {item.photos.length > 0 && <ReportPhotoSection photos={item.photos} />}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>

        {(detail.generalComment.trim() || detail.generalPhotos.length > 0) && (
          <section className="report-section report-section-separate-page space-y-4 rounded-2xl border border-slate-300 bg-slate-50/35 p-4 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
            <div className="report-section-header border-b border-slate-300 pb-3">
              <div className="flex items-center gap-3">
                <span className="h-8 w-2 rounded-full bg-emerald-500" />
                <h2 className="text-lg font-semibold text-slate-900">Общие комментарии и фото</h2>
              </div>
            </div>
            {detail.generalComment.trim() && (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-700">
                {renderFormattedComment(detail.generalComment.trim())}
              </div>
            )}
            {detail.generalPhotos.length > 0 && <ReportPhotoSection photos={detail.generalPhotos} />}
          </section>
        )}
      </section>
    </div>
  )
}

function ReportMetaCard({ label, value, className = '' }: { label: string; value: string; className?: string }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm ${className}`}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="mt-1.5 text-sm font-medium leading-6 text-slate-900">{value}</div>
    </div>
  )
}

function ReportPhotoSection({ photos }: { photos: TrainerChecklistPhoto[] }) {
  return (
    <section className="mt-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {photos.map(photo => (
          <a
            key={photo.id}
            href={photo.previewUrl}
            target="_blank"
            rel="noreferrer"
            className="group overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-sm"
          >
            <div className="relative aspect-[4/3] bg-slate-100">
              <img src={photo.previewUrl} alt="Фото отсмотра" className="h-full w-full object-cover" loading="eager" />
              <span className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-slate-600 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
                <ExternalLink className="h-3.5 w-3.5" />
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  )
}
